import React, { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, DollarSign, Mail, 
  UserPlus, Crown, Star, Clock,
  Download, RefreshCw, ChevronRight,
  BarChart3, PieChart, Activity,
  MessageSquare, Bug, Lightbulb, CheckCircle, Eye, Filter,
  Plus, Key, User, AtSign, Edit2, Trash2, X, Save, AlertTriangle
} from 'lucide-react';

const AdminDashboard = ({ user, API_URL }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [userFilter, setUserFilter] = useState('all');
  const [feedbackFilter, setFeedbackFilter] = useState({ category: 'all', status: 'all' });
  
  // Create User form state
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', plan: 'premium' });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserMessage, setCreateUserMessage] = useState(null);
  
  // Edit User state
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', plan: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editMessage, setEditMessage] = useState(null);
  
  // Delete User state
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, waitlistRes, revenueRes, feedbackRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`).then(r => r.json()),
        fetch(`${API_URL}/api/admin/users?limit=50`).then(r => r.json()),
        fetch(`${API_URL}/api/admin/waitlist?limit=100`).then(r => r.json()),
        fetch(`${API_URL}/api/admin/revenue`).then(r => r.json()),
        fetch(`${API_URL}/api/admin/feedback?limit=100`).then(r => r.json())
      ]);

      if (statsRes.success) setStats(statsRes.stats);
      if (usersRes.success) setUsers(usersRes.users);
      if (waitlistRes.success) setWaitlist(waitlistRes.entries);
      if (revenueRes.success) setRevenue(revenueRes.revenue);
      if (feedbackRes.success) {
        setFeedback(feedbackRes.feedback);
        setFeedbackStats(feedbackRes.stats);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportWaitlist = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Joined At'].join(','),
      ...waitlist.map(entry => [
        entry.name || '',
        entry.email || '',
        entry.phone || '',
        entry.joined_at || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportUsers = () => {
    const csv = [
      ['Name', 'Email', 'Plan', 'Created At'].join(','),
      ...users.map(user => [
        user.name || '',
        user.email || '',
        user.plan || 'free',
        user.created_at || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportFeedback = () => {
    const csv = [
      ['Category', 'Status', 'User', 'Email', 'Plan', 'Message', 'Submitted At'].join(','),
      ...feedback.map(f => [
        f.category || '',
        f.status || '',
        `"${(f.user_name || '').replace(/"/g, '""')}"`,
        f.user_email || '',
        f.user_plan || '',
        `"${(f.message || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
        f.created_at || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/feedback/${feedbackId}?status=${newStatus}`, {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.success) {
        // Update local state
        setFeedback(prev => prev.map(f => 
          f.id === feedbackId ? { ...f, status: newStatus } : f
        ));
        // Update stats
        if (feedbackStats) {
          setFeedbackStats(prev => {
            const oldStatus = feedback.find(f => f.id === feedbackId)?.status || 'new';
            return {
              ...prev,
              byStatus: {
                ...prev.byStatus,
                [oldStatus]: Math.max(0, (prev.byStatus[oldStatus] || 0) - 1),
                [newStatus]: (prev.byStatus[newStatus] || 0) + 1
              }
            };
          });
        }
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreateUserLoading(true);
    setCreateUserMessage(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/create-user?admin_email=${encodeURIComponent(user?.email || '')}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newUser)
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setCreateUserMessage({ type: 'success', text: `User ${newUser.email} created successfully with ${newUser.plan} plan!` });
        setNewUser({ email: '', password: '', name: '', plan: 'premium' });
        // Refresh user list
        fetchAllData();
      } else {
        setCreateUserMessage({ type: 'error', text: data.error || 'Failed to create user' });
      }
    } catch (error) {
      setCreateUserMessage({ type: 'error', text: 'Failed to create user. Please try again.' });
    } finally {
      setCreateUserLoading(false);
    }
  };

  // Start editing a user
  const startEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      plan: userToEdit.plan || 'free'
    });
    setEditMessage(null);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({ name: '', email: '', plan: '' });
    setEditMessage(null);
  };

  // Save user edits
  const saveUserEdit = async () => {
    if (!editingUser) return;
    
    setEditLoading(true);
    setEditMessage(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users/${editingUser.id}?admin_email=${encodeURIComponent(user?.email || '')}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editForm)
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setEditMessage({ type: 'success', text: 'User updated successfully!' });
        // Update local state
        setUsers(prev => prev.map(u => 
          u.id === editingUser.id 
            ? { ...u, ...editForm }
            : u
        ));
        // Close modal after short delay
        setTimeout(() => {
          cancelEdit();
          fetchAllData();
        }, 1000);
      } else {
        setEditMessage({ type: 'error', text: data.error || 'Failed to update user' });
      }
    } catch (error) {
      setEditMessage({ type: 'error', text: 'Failed to update user. Please try again.' });
    } finally {
      setEditLoading(false);
    }
  };

  // Start delete confirmation
  const startDeleteUser = (userToDelete) => {
    setDeletingUser(userToDelete);
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeletingUser(null);
  };

  // Confirm and delete user
  const confirmDeleteUser = async () => {
    if (!deletingUser) return;
    
    setDeleteLoading(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users/${deletingUser.id}?admin_email=${encodeURIComponent(user?.email || '')}`,
        {
          method: 'DELETE'
        }
      );
      const data = await response.json();
      
      if (data.success) {
        // Remove from local state
        setUsers(prev => prev.filter(u => u.id !== deletingUser.id));
        cancelDelete();
        fetchAllData();
      } else {
        alert(data.error || 'Failed to delete user');
      }
    } catch (error) {
      alert('Failed to delete user. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-3 flex items-center gap-1">
          <TrendingUp className={`w-4 h-4 ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
          <span className={`text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
          <span className="text-xs text-gray-400 ml-1">vs last week</span>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Real-time analytics and user management</p>
        </div>
        <button
          onClick={fetchAllData}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'users', label: 'Users', icon: Users },
          { id: 'create-user', label: 'Create User', icon: UserPlus },
          { id: 'feedback', label: 'Feedback', icon: MessageSquare, badge: feedbackStats?.byStatus?.new || 0 },
          { id: 'waitlist', label: 'Waitlist', icon: Mail },
          { id: 'revenue', label: 'Revenue', icon: DollarSign }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 -mb-[2px] whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Total Users"
              value={stats.totalUsers}
              subValue={`+${stats.recentSignups} this week`}
              color="bg-blue-500"
            />
            <StatCard
              icon={Crown}
              label="Premium Users"
              value={stats.usersByPlan?.premium || 0}
              subValue="Highest tier subscribers"
              color="bg-purple-500"
            />
            <StatCard
              icon={Mail}
              label="Waitlist"
              value={stats.waitlistCount}
              subValue="Potential customers"
              color="bg-green-500"
            />
            <StatCard
              icon={DollarSign}
              label="Monthly Revenue"
              value={`$${stats.mrr?.toLocaleString() || 0}`}
              subValue="MRR"
              color="bg-amber-500"
            />
          </div>

          {/* User Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-600" />
                Users by Plan
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'Free', value: stats.usersByPlan?.free || 0, color: 'bg-gray-400', percent: stats.totalUsers ? Math.round((stats.usersByPlan?.free || 0) / stats.totalUsers * 100) : 0 },
                  { label: 'Basic ($29/mo)', value: stats.usersByPlan?.basic || 0, color: 'bg-blue-500', percent: stats.totalUsers ? Math.round((stats.usersByPlan?.basic || 0) / stats.totalUsers * 100) : 0 },
                  { label: 'Premium ($79/mo)', value: stats.usersByPlan?.premium || 0, color: 'bg-purple-500', percent: stats.totalUsers ? Math.round((stats.usersByPlan?.premium || 0) / stats.totalUsers * 100) : 0 }
                ].map(item => (
                  <div key={item.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-medium text-gray-900">{item.value} ({item.percent}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${item.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Signup Trend */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Signup Trend (Last 7 Days)
              </h3>
              <div className="flex items-end justify-between h-32 gap-2">
                {stats.signupTrend?.map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-purple-500 rounded-t transition-all hover:bg-purple-600"
                      style={{ height: `${Math.max(day.signups * 20, 4)}px` }}
                    />
                    <span className="text-xs text-gray-500 mt-2">{day.date}</span>
                    <span className="text-xs font-medium text-gray-700">{day.signups}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.monthlySignups}</p>
              <p className="text-sm text-blue-600">Signups (30d)</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{stats.activeSubscriptions}</p>
              <p className="text-sm text-green-600">Active Subs</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">${(stats.mrr * 12).toLocaleString()}</p>
              <p className="text-sm text-purple-600">ARR</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">
                {stats.totalUsers > 0 ? Math.round(((stats.usersByPlan?.basic || 0) + (stats.usersByPlan?.premium || 0)) / stats.totalUsers * 100) : 0}%
              </p>
              <p className="text-sm text-amber-600">Conversion Rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2 flex-wrap">
              {['all', 'free', 'basic', 'premium'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setUserFilter(filter)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    userFilter === filter
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
            <button
              onClick={exportUsers}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users
                    .filter(u => userFilter === 'all' || u.plan === userFilter)
                    .map((userItem, i) => (
                    <tr key={userItem.id || i} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-purple-600">
                              {(userItem.name || userItem.email || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{userItem.name || 'No name'}</p>
                            <p className="text-xs text-gray-500 truncate">{userItem.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          userItem.plan === 'premium' ? 'bg-purple-100 text-purple-700' :
                          userItem.plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {userItem.plan || 'Free'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {userItem.created_at ? new Date(userItem.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Active
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => startEditUser(userItem)}
                            className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Edit user"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => startDeleteUser(userItem)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <div className="text-center py-8 text-gray-500">No users found</div>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5" />
                Edit User
              </h3>
              <button onClick={cancelEdit} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {editMessage && (
                <div className={`p-4 rounded-xl text-sm ${
                  editMessage.type === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {editMessage.text}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <AtSign className="w-4 h-4 inline mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Crown className="w-4 h-4 inline mr-1" />
                  Subscription Plan
                </label>
                <select
                  value={editForm.plan}
                  onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                >
                  <option value="premium">Premium (Full Access)</option>
                  <option value="basic">Basic</option>
                  <option value="free">Free</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={cancelEdit}
                  className="flex-1 py-2.5 px-4 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveUserEdit}
                  disabled={editLoading}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                    editLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                  }`}
                >
                  {editLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete User
              </h3>
              <button onClick={cancelDelete} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{deletingUser.name || 'No name'}</p>
                  <p className="text-sm text-gray-500">{deletingUser.email}</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-2">
                Are you sure you want to delete this user? This action will:
              </p>
              <ul className="text-sm text-gray-500 mb-6 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                  Remove all user data
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                  Delete all their listings
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                  Delete all their documents
                </li>
              </ul>
              
              <p className="text-sm text-red-600 font-medium mb-4">
                This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 py-2.5 px-4 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteUser}
                  disabled={deleteLoading}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                    deleteLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {deleteLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Tab */}
      {activeTab === 'create-user' && (
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Create New User
              </h3>
              <p className="text-white/80 text-sm mt-1">Add a new user with specified access level</p>
            </div>
            
            <form onSubmit={createUser} className="p-6 space-y-4">
              {createUserMessage && (
                <div className={`p-4 rounded-xl text-sm ${
                  createUserMessage.type === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {createUserMessage.text}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                  placeholder="John Doe"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <AtSign className="w-4 h-4 inline mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  placeholder="user@example.com"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Key className="w-4 h-4 inline mr-1" />
                  Password
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  minLength={6}
                  placeholder="Minimum 6 characters"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Crown className="w-4 h-4 inline mr-1" />
                  Subscription Plan
                </label>
                <select
                  value={newUser.plan}
                  onChange={(e) => setNewUser({ ...newUser, plan: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                >
                  <option value="premium">Premium (Full Access)</option>
                  <option value="basic">Basic</option>
                  <option value="free">Free</option>
                </select>
              </div>
              
              <button
                type="submit"
                disabled={createUserLoading}
                className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                  createUserLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {createUserLoading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" />
                    Create User
                  </>
                )}
              </button>
            </form>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Users created here will receive the specified plan immediately. 
              They can log in using the email and password you set.
            </p>
          </div>
        </div>
      )}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && (
        <div className="space-y-4">
          {/* Stats Cards */}
          {feedbackStats && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
                <Bug className="w-5 h-5 text-red-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-red-700">{feedbackStats.byCategory?.bug || 0}</p>
                <p className="text-xs text-red-600">Bug Reports</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                <Lightbulb className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-amber-700">{feedbackStats.byCategory?.feature || 0}</p>
                <p className="text-xs text-amber-600">Feature Requests</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                <MessageSquare className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-blue-700">{feedbackStats.byCategory?.general || 0}</p>
                <p className="text-xs text-blue-600">General</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-100">
                <Clock className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-purple-700">{feedbackStats.byStatus?.new || 0}</p>
                <p className="text-xs text-purple-600">New</p>
              </div>
              <div className="bg-cyan-50 rounded-xl p-3 text-center border border-cyan-100">
                <Eye className="w-5 h-5 text-cyan-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-cyan-700">{feedbackStats.byStatus?.reviewed || 0}</p>
                <p className="text-xs text-cyan-600">Reviewed</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
                <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-green-700">{feedbackStats.byStatus?.resolved || 0}</p>
                <p className="text-xs text-green-600">Resolved</p>
              </div>
            </div>
          )}

          {/* Filters and Export */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <Filter className="w-4 h-4 text-gray-500 ml-2" />
                {['all', 'bug', 'feature', 'general'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setFeedbackFilter(prev => ({ ...prev, category: cat }))}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                      feedbackFilter.category === cat
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat === 'all' ? 'All Types' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                {['all', 'new', 'reviewed', 'resolved'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFeedbackFilter(prev => ({ ...prev, status: status }))}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                      feedbackFilter.status === status
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={exportFeedback}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          {/* Feedback List */}
          <div className="space-y-3">
            {feedback
              .filter(f => feedbackFilter.category === 'all' || f.category === feedbackFilter.category)
              .filter(f => feedbackFilter.status === 'all' || f.status === feedbackFilter.status)
              .map((item, i) => {
                const categoryConfig = {
                  bug: { icon: Bug, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', label: 'Bug Report' },
                  feature: { icon: Lightbulb, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Feature Request' },
                  general: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', label: 'General Feedback' }
                };
                const config = categoryConfig[item.category] || categoryConfig.general;
                const CategoryIcon = config.icon;
                
                const statusConfig = {
                  new: { color: 'bg-purple-100 text-purple-700', label: 'New' },
                  reviewed: { color: 'bg-cyan-100 text-cyan-700', label: 'Reviewed' },
                  resolved: { color: 'bg-green-100 text-green-700', label: 'Resolved' }
                };
                const statusStyle = statusConfig[item.status] || statusConfig.new;
                
                return (
                  <div key={item.id || i} className={`bg-white rounded-xl p-4 shadow-sm border ${config.border} hover:shadow-md transition-shadow`}>
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${config.bg}`}>
                        <CategoryIcon className={`w-5 h-5 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
                              {config.label}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.color}`}>
                              {statusStyle.label}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              item.user_plan === 'premium' ? 'bg-purple-100 text-purple-700' :
                              item.user_plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {item.user_plan || 'Free'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap">{item.message}</p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            <span className="font-medium">{item.user_name}</span> • {item.user_email}
                          </p>
                          {/* Status Update Buttons */}
                          <div className="flex items-center gap-1">
                            {item.status !== 'reviewed' && (
                              <button
                                onClick={() => updateFeedbackStatus(item.id, 'reviewed')}
                                className="px-2 py-1 text-xs font-medium text-cyan-600 bg-cyan-50 rounded-md hover:bg-cyan-100 transition-colors"
                                title="Mark as Reviewed"
                              >
                                <Eye className="w-3 h-3 inline mr-1" />
                                Review
                              </button>
                            )}
                            {item.status !== 'resolved' && (
                              <button
                                onClick={() => updateFeedbackStatus(item.id, 'resolved')}
                                className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                                title="Mark as Resolved"
                              >
                                <CheckCircle className="w-3 h-3 inline mr-1" />
                                Resolve
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            {feedback.filter(f => 
              (feedbackFilter.category === 'all' || f.category === feedbackFilter.category) &&
              (feedbackFilter.status === 'all' || f.status === feedbackFilter.status)
            ).length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No feedback found</p>
                <p className="text-sm text-gray-400 mt-1">Feedback from users will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Waitlist Tab */}
      {activeTab === 'waitlist' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{waitlist.length} people on waitlist</p>
            <button
              onClick={exportWaitlist}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {waitlist.map((entry, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 text-sm">{entry.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{entry.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {entry.joined_at ? new Date(entry.joined_at).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {waitlist.length === 0 && (
              <div className="text-center py-8 text-gray-500">No waitlist entries yet</div>
            )}
          </div>
        </div>
      )}

      {/* Revenue Tab */}
      {activeTab === 'revenue' && revenue && (
        <div className="space-y-6">
          {/* Revenue Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
              <p className="text-green-100 text-sm mb-1">Monthly Recurring Revenue</p>
              <p className="text-3xl font-bold">${revenue.mrr.toLocaleString()}</p>
              <p className="text-green-200 text-sm mt-2">MRR</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
              <p className="text-purple-100 text-sm mb-1">Annual Recurring Revenue</p>
              <p className="text-3xl font-bold">${revenue.arr.toLocaleString()}</p>
              <p className="text-purple-200 text-sm mt-2">ARR</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <p className="text-blue-100 text-sm mb-1">Total Paid Users</p>
              <p className="text-3xl font-bold">{revenue.breakdown.basic.count + revenue.breakdown.premium.count}</p>
              <p className="text-blue-200 text-sm mt-2">Subscribers</p>
            </div>
          </div>

          {/* Plan Breakdown */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-4">Revenue by Plan</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">Basic Plan</p>
                    <p className="text-sm text-gray-500">${revenue.breakdown.basic.price}/month</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{revenue.breakdown.basic.count} users</p>
                  <p className="text-sm text-blue-600">${revenue.breakdown.basic.mrr}/mo</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-gray-900">Premium Plan</p>
                    <p className="text-sm text-gray-500">${revenue.breakdown.premium.price}/month</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{revenue.breakdown.premium.count} users</p>
                  <p className="text-sm text-purple-600">${revenue.breakdown.premium.mrr}/mo</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-4">New Subscriptions (Last 30 Days)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{revenue.recentSubscriptions.basic}</p>
                <p className="text-sm text-gray-500">New Basic</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{revenue.recentSubscriptions.premium}</p>
                <p className="text-sm text-gray-500">New Premium</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
